/**
 * Vanilla Cookie Notice
 *
 * Slim bottom bar rendered as plain DOM, the `notice` surface of the four
 * first-visit surfaces the core derives (`pending` and `none` render
 * nothing, `banner` renders `banner.ts`, `notice` renders this). It mirrors
 * the marketing site's notice: analytics is on by regional default and
 * advertising is off, so the notice informs rather than asks.
 *
 * There is deliberately no Reject All: nothing was asked, so nothing is
 * refused. "Cookie Preferences" opens the Customize modal, which is the
 * opt-out. OK calls the core's `acknowledge()`, which records the seeded
 * categories as the visitor's decision so the notice never returns.
 */

import { acknowledge, showCustomize } from "./core";

/** DOM id of the notice root (E2E contract). */
export const NOTICE_ELEMENT_ID = "as-cookie-notice";

export interface NoticeMountOptions {
  /** Privacy Policy link target; defaults to `/privacy-policy`. */
  privacyPolicyHref?: string;
}

export interface NoticeHandle {
  unmount: () => void;
}

const LINK_CLASS =
  "font-medium text-text-teal-700 underline hover:text-text-teal-800";

/**
 * Mount the notice into the given container. Calling `mountNotice` twice
 * is a no-op; the existing handle is returned instead so the entrypoint
 * can idempotently react to core state transitions.
 *
 * @param container - usually `document.body`; defaults as such
 * @param options - optional overrides (e.g. a custom privacy href)
 * @returns a handle exposing `unmount()`
 */
export function mountNotice(
  container: HTMLElement = document.body,
  options: NoticeMountOptions = {},
): NoticeHandle {
  const existing = document.getElementById(NOTICE_ELEMENT_ID);
  if (existing) {
    return { unmount: () => existing.remove() };
  }

  const root = document.createElement("section");
  root.id = NOTICE_ELEMENT_ID;
  root.setAttribute("role", "region");
  root.setAttribute("aria-label", "Cookie notice");
  root.setAttribute("aria-live", "polite");
  root.className =
    "fixed inset-x-0 bottom-0 z-[9999] border-t border-border-base bg-surface-card text-text-body shadow-xl";

  // One row on every width: the sentence wraps beside OK.
  const row = document.createElement("div");
  row.className =
    "mx-auto flex max-w-7xl flex-row items-center gap-3 px-4 py-2 sm:px-6 md:gap-4 md:py-3";
  root.appendChild(row);

  const copy = document.createElement("p");
  copy.className =
    "min-w-0 flex-1 text-xs leading-snug text-text-body md:text-sm";
  copy.appendChild(
    document.createTextNode(
      "We use analytics cookies to understand how the site is used. Advertising cookies are off. Change this any time in ",
    ),
  );

  const preferences = document.createElement("button");
  preferences.type = "button";
  preferences.className = "rounded " + LINK_CLASS;
  preferences.textContent = "Cookie Preferences";
  preferences.addEventListener("click", () => showCustomize());
  copy.appendChild(preferences);

  copy.appendChild(document.createTextNode(" · "));

  const privacy = document.createElement("a");
  privacy.href = options.privacyPolicyHref ?? "/privacy-policy";
  privacy.className = LINK_CLASS;
  privacy.textContent = "Privacy Policy";
  copy.appendChild(privacy);
  row.appendChild(copy);

  // Same fill and size as the banner's Accept All; focus comes from the global halo.
  const ok = document.createElement("button");
  ok.type = "button";
  ok.className =
    "inline-flex shrink-0 items-center justify-center rounded-lg bg-fixed-teal-600 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-fixed-teal-700 motion-reduce:transition-none md:py-2";
  ok.textContent = "OK";
  ok.addEventListener("click", () => {
    void acknowledge();
  });
  row.appendChild(ok);

  container.appendChild(root);

  return { unmount: unmountNotice };
}

/**
 * Remove the notice from the DOM. Idempotent.
 */
export function unmountNotice(): void {
  document.getElementById(NOTICE_ELEMENT_ID)?.remove();
}
